import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { usePostcodeLookup } from "@/hooks/usePostcodeLookup";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Building2, User, ArrowRight, ArrowLeft, Check, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface CompanyData {
  name: string;
  address: string;
  house_number: string;
  city: string;
  postal_code: string;
  country: string;
}

interface ProfileData {
  first_name: string;
  last_name: string;
}

interface OnboardingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  required?: boolean;
}

const OnboardingDialog = ({ open, onOpenChange, required = false }: OnboardingDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  const [company, setCompany] = useState<CompanyData>({
    name: "", address: "", house_number: "", city: "", postal_code: "", country: "Nederland",
  });

  const [profile, setProfile] = useState<ProfileData>({ first_name: "", last_name: "" });
  const [phoneCountryCode, setPhoneCountryCode] = useState("+31");
  const [phoneLocal, setPhoneLocal] = useState("");

  // Pre-fill from profile when dialog opens
  useEffect(() => {
    if (!open || !user || prefilled) return;

    const prefillData = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, company_name, phone")
        .eq("user_id", user.id)
        .maybeSingle();

      if (data) {
        setProfile({ first_name: data.first_name || "", last_name: data.last_name || "" });
        if (data.phone) {
          const match = data.phone.match(/^(\+\d{1,3})\s*(.*)$/);
          if (match) {
            setPhoneCountryCode(match[1]);
            setPhoneLocal(match[2]);
          } else {
            setPhoneLocal(data.phone);
          }
        }
        if (data.company_name) {
          setCompany((prev) => ({ ...prev, name: data.company_name || "" }));
        }
      }
      setPrefilled(true);
    };

    prefillData();
  }, [open, user, prefilled]);

  const { lookup, isLoading: isLookingUp } = usePostcodeLookup();

  const handleCompanyChange = (field: keyof CompanyData, value: string) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  // Auto-lookup postcode
  useEffect(() => {
    const doLookup = async () => {
      const cleaned = company.postal_code.replace(/\s/g, "");
      if (cleaned.length >= 6 && company.house_number.trim()) {
        setLookupFailed(false);
        const result = await lookup(company.postal_code, company.house_number);
        if (result) {
          setCompany((prev) => ({ ...prev, address: result.street, city: result.city }));
        } else {
          setLookupFailed(true);
        }
      } else {
        setLookupFailed(false);
      }
    };
    doLookup();
  }, [company.postal_code, company.house_number, lookup]);

  const isCompanyValid = () =>
    company.name.trim() !== "" &&
    company.address.trim() !== "" &&
    company.house_number.trim() !== "" &&
    company.country.trim() !== "";

  const isProfileValid = () =>
    profile.first_name.trim() !== "" && profile.last_name.trim() !== "";

  const handleComplete = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { data: existingMember } = await supabase
        .from("company_members")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();

      let companyId = existingMember?.company_id;

      if (companyId) {
        await supabase
          .from("companies")
          .update({
            name: company.name,
            address: company.address,
            house_number: company.house_number,
            city: company.city,
            postal_code: company.postal_code,
            country: company.country,
          })
          .eq("id", companyId);
      } else {
        companyId = crypto.randomUUID();

        // Retry on rare customer_number race conditions (23505 = unique_violation)
        let companyError: any = null;
        for (let attempt = 0; attempt < 5; attempt++) {
          const { error } = await supabase
            .from("companies")
            .insert({
              id: companyId,
              customer_number: "",
              name: company.name,
              address: company.address,
              house_number: company.house_number,
              city: company.city,
              postal_code: company.postal_code,
              country: company.country,
            });
          if (!error) { companyError = null; break; }
          companyError = error;
          // Only retry on customer_number unique violation
          const isCustomerNumberDup =
            (error as any).code === "23505" &&
            String((error as any).message || "").includes("customer_number");
          if (!isCustomerNumberDup) break;
          // New uuid for next attempt to avoid pk conflict if first row was actually inserted
          companyId = crypto.randomUUID();
          await new Promise((r) => setTimeout(r, 100));
        }
        if (companyError) throw companyError;

        const { error: memberError } = await supabase
          .from("company_members")
          .insert({ company_id: companyId, user_id: user.id, role: "owner" });
        if (memberError) throw memberError;
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          full_name: `${profile.first_name} ${profile.last_name}`.trim(),
          phone: phoneLocal ? `${phoneCountryCode} ${phoneLocal}`.trim() : null,
          company_name: company.name,
          company_address: company.address,
          company_house_number: company.house_number,
          company_city: company.city,
          company_postal_code: company.postal_code,
          company_country: company.country,
          onboarding_completed: true,
        } as any)
        .eq("user_id", user.id);

      if (profileError) throw profileError;

      await queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });

      toast({ title: t("onboarding.welcome"), description: t("onboarding.welcomeDesc") });
      onOpenChange(false);
    } catch (error: any) {
      toast({ title: t("onboarding.saveError"), description: error.message, variant: "destructive" });
    }

    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={required ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg" onInteractOutside={required ? (e) => e.preventDefault() : undefined}>
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-3 pt-2 pb-4">
          <div className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            <Building2 className="w-4 h-4" />
          </div>
          <div className={`w-12 h-1 rounded transition-colors ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            <User className="w-4 h-4" />
          </div>
        </div>

        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {t("onboarding.companyDetails")}
              </DialogTitle>
              <DialogDescription>
                {required
                  ? t("onboarding.companyDescRequired")
                  : t("onboarding.companyDescOptional")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="ob-companyName">{t("onboarding.companyName")}</Label>
                <Input
                  id="ob-companyName"
                  value={company.name}
                  onChange={(e) => handleCompanyChange("name", e.target.value)}
                  placeholder="Pharma Labs B.V."
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ob-postalCode">{t("onboarding.postalCode")}</Label>
                  <Input
                    id="ob-postalCode"
                    value={company.postal_code}
                    onChange={(e) => handleCompanyChange("postal_code", e.target.value)}
                    placeholder="1012 AB"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-houseNumber">{t("onboarding.houseNumber")}</Label>
                  <div className="relative">
                    <Input
                      id="ob-houseNumber"
                      value={company.house_number}
                      onChange={(e) => handleCompanyChange("house_number", e.target.value)}
                      placeholder="123"
                    />
                    {isLookingUp && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-city">{t("onboarding.city")}</Label>
                  <Input
                    id="ob-city"
                    value={company.city}
                    onChange={(e) => handleCompanyChange("city", e.target.value)}
                    placeholder="Amsterdam"
                    className="bg-muted/50"
                  />
                </div>
              </div>

              {lookupFailed && (
                <p className="text-sm text-destructive">
                  {t("onboarding.lookupFailed")}
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="ob-address">{t("onboarding.streetName")}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="ob-address"
                    value={company.address}
                    onChange={(e) => handleCompanyChange("address", e.target.value)}
                    placeholder={t("onboarding.autoFill")}
                    className="pl-9 bg-muted/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ob-country">{t("onboarding.country")}</Label>
                <Input
                  id="ob-country"
                  value={company.country}
                  onChange={(e) => handleCompanyChange("country", e.target.value)}
                  placeholder="Nederland"
                />
              </div>

              <div className="flex gap-3 pt-2">
                {!required && (
                  <Button variant="ghost" onClick={() => onOpenChange(false)} className="flex-1">
                    {t("onboarding.fillLater")}
                  </Button>
                )}
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => setStep(2)}
                  disabled={!isCompanyValid()}
                >
                  {t("onboarding.next")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t("onboarding.personalDetails")}
              </DialogTitle>
              <DialogDescription>{t("onboarding.personalDesc")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ob-firstName">{t("onboarding.firstName")}</Label>
                  <Input
                    id="ob-firstName"
                    value={profile.first_name}
                    onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                    placeholder="Jan"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-lastName">{t("onboarding.lastName")}</Label>
                  <Input
                    id="ob-lastName"
                    value={profile.last_name}
                    onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                    placeholder="de Vries"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ob-email">{t("onboarding.email")}</Label>
                <Input id="ob-email" value={user?.email || ""} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">{t("onboarding.emailLinked")}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ob-phone">{t("onboarding.phone")}</Label>
                <div className="flex gap-2">
                  <select
                    value={phoneCountryCode}
                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                    className="flex h-10 w-[90px] shrink-0 rounded-md border border-input bg-background px-2 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="+31">🇳🇱 +31</option>
                    <option value="+32">🇧🇪 +32</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+41">🇨🇭 +41</option>
                    <option value="+43">🇦🇹 +43</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+39">🇮🇹 +39</option>
                  </select>
                  <Input
                    id="ob-phone"
                    value={phoneLocal}
                    onChange={(e) => setPhoneLocal(e.target.value)}
                    placeholder="6 12345678"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("onboarding.back")}
                </Button>
                <Button
                  variant="default"
                  onClick={handleComplete}
                  disabled={!isProfileValid() || loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t("onboarding.saving")}
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {t("onboarding.complete")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingDialog;
